import type { EntityDiscoveryItem, EntityRelationship } from '../../api/types';

export interface RelatedEntity {
  entity: EntityDiscoveryItem;
  relationships: EntityRelationship[];
}

export type EntityRelationshipIndex = Map<string, RelatedEntity[]>;

/** Build a bidirectional, table-deduplicated relationship index for discovered entities. */
export function indexEntityRelationships(
  entities: EntityDiscoveryItem[],
  relationships: EntityRelationship[],
): EntityRelationshipIndex {
  const entitiesByName = new Map(entities.map(entity => [entity.name, entity]));
  const relatedByEntity = new Map<string, Map<string, EntityRelationship[]>>();

  for (const relationship of relationships) {
    if (relationship.fromEntity === relationship.toEntity) continue;
    if (!entitiesByName.has(relationship.fromEntity) || !entitiesByName.has(relationship.toEntity)) continue;

    for (const [entityName, relatedName] of [
      [relationship.fromEntity, relationship.toEntity],
      [relationship.toEntity, relationship.fromEntity],
    ] as const) {
      const related = relatedByEntity.get(entityName) ?? new Map<string, EntityRelationship[]>();
      const matches = related.get(relatedName) ?? [];
      matches.push(relationship);
      related.set(relatedName, matches);
      relatedByEntity.set(entityName, related);
    }
  }

  return new Map([...relatedByEntity].map(([entityName, related]) => [
    entityName,
    [...related].map(([relatedName, matches]) => ({
      entity: entitiesByName.get(relatedName)!,
      relationships: matches,
    })).sort((left, right) => left.entity.label.localeCompare(right.entity.label)),
  ]));
}

export function relationshipJoinLabel(relationship: EntityRelationship, entityName: string): string {
  const pairs = relationship.columnPairs?.length
    ? relationship.columnPairs
    : [{ from_column: relationship.fromField, to_column: relationship.toField }];
  const reverse = relationship.toEntity === entityName;
  return pairs.map(pair => reverse
    ? `${pair.to_column} ↔ ${pair.from_column}`
    : `${pair.from_column} ↔ ${pair.to_column}`).join(', ');
}
